import {makeAutoObservable, observable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IContest, IContestsStore} from "data/stores/contests/contests.store";
import type {ISquad, ISquadsStore} from "data/stores/squads/squads.store";

interface IProps {
	gameFeedId: number;
}

export interface IContestInfoController extends ViewController<IProps> {
	readonly i18n: ILocalizationStore;

	get currentContest(): IContest | undefined;
	get awaySquad(): ISquad | undefined;
	get homeSquad(): ISquad | undefined;
	get homeSquadResults(): string | undefined;
	get awaySquadResults(): string | undefined;
}

@injectable()
export class ContestInfoController implements IContestInfoController {
	@observable _gameFeedId: number = 0;

	get currentContest() {
		return this._contestsStore.getContestByFeedId(this._gameFeedId);
	}

	get awaySquad() {
		if (!this.currentContest) return;

		return this._squadsStore.getSquadById(this.currentContest.awaySquadId);
	}

	get homeSquad() {
		if (!this.currentContest) return;

		return this._squadsStore.getSquadById(this.currentContest.homeSquadId);
	}

	get homeSquadResults() {
		if (!this.currentContest) return;

		const squad = this._squadsStore.getSquadById(this.currentContest.homeSquadId);

		if (!squad) return "";

		return `${squad?.wins}-${squad?.losses}`;
	}

	get awaySquadResults() {
		if (!this.currentContest) return;

		const squad = this._squadsStore.getSquadById(this.currentContest.awaySquadId);

		if (!squad) return "";

		return `${squad?.wins}-${squad?.losses}`;
	}

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.ContestsStore) private _contestsStore: IContestsStore,
		@inject(Bindings.SquadsStore) private _squadsStore: ISquadsStore
	) {
		makeAutoObservable(this);
	}

	init({gameFeedId}: IProps) {
		this._gameFeedId = gameFeedId;
	}
}
